import * as React from "react";
import { cn } from "../../lib/utils.jsx";

type Variant =
  | "default"
  | "destructive"
  | "outline"
  | "secondary"
  | "ghost"
  | "link"
  | "gradient";
type Size = "default" | "sm" | "lg" | "icon";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const base =
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus:outline-none disabled:opacity-60 disabled:pointer-events-none";
const sizes: Record<Size, string> = {
  default: "h-9 px-4 py-2",
  sm: "h-8 px-3",
  lg: "h-10 px-6",
  icon: "h-9 w-9 px-2",
};
const variants: Record<Variant, string> = {
  default: "bg-indigo-600 text-white hover:bg-indigo-700",
  destructive: "bg-red-600 text-white hover:bg-red-700",
  outline: "border border-white/20 bg-transparent hover:bg-white/10 text-white",
  secondary: "bg-white/10 text-white hover:bg-white/15",
  ghost: "bg-transparent hover:bg-white/10 text-white",
  link: "bg-transparent underline-offset-4 hover:underline text-indigo-300",
  gradient:
    "bg-gradient-to-br from-indigo-500 to-sky-500 text-white shadow hover:shadow-lg",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(base, sizes[size], variants[variant], className)}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export default Button;
